import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  TablePagination,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import useUserReturns from "../hooks/useUserReturns";
import { format } from "date-fns";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Helper to get chip color based on return status
const getReturnStatusChipColor = (status) => {
  switch (status?.toLowerCase()) {
    case "requested":
      return "warning";
    case "under_review":
      return "info";
    case "approved":
    case "partially_approved":
      return "secondary";
    case "shipped_by_user":
      return "info";
    case "received":
      return "primary";
    case "completed":
      return "success";
    case "rejected":
      return "error";
    default:
      return "default";
  }
};

/**
 * User Returns Page component
 * Displays a list of the user's returns and allows filtering
 * @returns {JSX.Element} The ReturnsPage component
 */
const ReturnsPage = () => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("returns");

  const {
    returns,
    totalReturns,
    loading,
    error,
    currentPage,
    rowsPerPage,
    statusFilter,
    setCurrentPage,
    setRowsPerPage,
    setStatusFilter,
    loadReturns,
    status,
  } = useUserReturns();

  // Log current state for debugging
  useEffect(() => {
    console.log("ReturnsPage - Current state:", {
      returns: returns?.length,
      totalReturns,
      status,
      statusFilter,
      currentPage,
    });
  }, [returns, totalReturns, status, statusFilter, currentPage]);

  const handleChangePage = (event, newPage) => {
    console.log("ReturnsPage - handleChangePage:", newPage);
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    console.log("ReturnsPage - handleChangeRowsPerPage:", value);
    setRowsPerPage(value);
    setCurrentPage(0);
  };

  const handleStatusFilterChange = (event) => {
    const value = event.target.value;
    console.log("ReturnsPage - handleStatusFilterChange:", value);
    setStatusFilter(value);
    setCurrentPage(0);
  };

  const isLoading = status === "loading";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          {t("user.list.title")}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error?.message || tCommon("status.error")}
        </Alert>
      )}

      {/* Filter controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="status-filter-label">
              {t("user.list.filters.status")}
            </InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusFilter || ""}
              label={t("user.list.filters.status")}
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">{t("user.list.filters.allStatuses")}</MenuItem>
              <MenuItem value="requested">
                {t("shared.status.requested")}
              </MenuItem>
              <MenuItem value="under_review">
                {t("shared.status.under_review")}
              </MenuItem>
              <MenuItem value="approved">
                {t("shared.status.approved")}
              </MenuItem>
              <MenuItem value="partially_approved">
                {t("shared.status.partially_approved")}
              </MenuItem>
              <MenuItem value="shipped_by_user">
                {t("shared.status.shipped_by_user")}
              </MenuItem>
              <MenuItem value="received">
                {t("shared.status.received")}
              </MenuItem>
              <MenuItem value="completed">
                {t("shared.status.completed")}
              </MenuItem>
              <MenuItem value="rejected">
                {t("shared.status.rejected")}
              </MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/orders"
            endIcon={<ArrowForwardIcon />}
          >
            {t("user.list.viewOrders")}
          </Button>
        </Box>
      </Paper>

      {/* Returns Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("user.list.table.returnId")}</TableCell>
                <TableCell>{t("user.list.table.orderNumber")}</TableCell>
                <TableCell>{t("user.list.table.date")}</TableCell>
                <TableCell>{t("user.list.table.status")}</TableCell>
                <TableCell>{t("user.list.table.items")}</TableCell>
                <TableCell align="right">
                  {t("user.list.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : returns && returns.length > 0 ? (
                returns.map((returnItem) => (
                  <TableRow key={returnItem.id}>
                    <TableCell>{returnItem.id.substring(0, 8)}</TableCell>
                    <TableCell>
                      <RouterLink to={`/orders/${returnItem.orderId}`}>
                        {returnItem.order?.orderNumber ||
                          returnItem.orderId.substring(0, 8)}
                      </RouterLink>
                    </TableCell>
                    <TableCell>
                      {returnItem.createdAt
                        ? format(new Date(returnItem.createdAt), "PP")
                        : tCommon("status.notApplicable", "N/A")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(
                          `shared.status.${returnItem.status?.toLowerCase()}`,
                          returnItem.status || t("shared.status.unknown")
                        )}
                        color={getReturnStatusChipColor(returnItem.status)}
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>{returnItem.items?.length || 0}</TableCell>
                    <TableCell align="right">
                      <Button
                        component={RouterLink}
                        to={`/returns/${returnItem.id}`}
                        startIcon={<VisibilityIcon />}
                        size="small"
                      >
                        {t("user.list.table.viewDetails")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    {statusFilter
                      ? t("user.list.table.noMatchingReturns")
                      : t("user.list.table.noReturns")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination controls */}
        {totalReturns > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalReturns}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={tCommon("pagination.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${tCommon("pagination.of", "of")} ${count}`
            }
          />
        )}
      </Paper>
    </Container>
  );
};

export default ReturnsPage;
